import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { ChevronDown, Edit3, Plus, Trash2, History, X } from "lucide-react";

const STATUS_DETAILS = {
  NC: {
    label: "NC",
    description: "Not compliant",
    badge: "bg-rose-100 text-rose-700 border border-rose-200",
    roadmapBadge:
      "border-2 border-dashed border-rose-300 text-rose-600 bg-white",
  },
  PC: {
    label: "PC",
    description: "Partially compliant",
    badge: "bg-amber-100 text-amber-700 border border-amber-200",
    roadmapBadge:
      "border-2 border-dashed border-amber-300 text-amber-600 bg-white",
  },
  CE: {
    label: "CE",
    description: "Compliance expected",
    badge: "bg-blue-100 text-blue-700 border border-blue-200",
    roadmapBadge:
      "border-2 border-dashed border-blue-300 text-blue-600 bg-white",
  },
  C: {
    label: "C",
    description: "Compliant",
    badge: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    roadmapBadge:
      "border-2 border-dashed border-emerald-300 text-emerald-600 bg-white",
  },
};

const STATUS_ORDER = ["NC", "PC", "CE", "C"];

const EVENT_LABELS = {
  created: "Attribut ajoute",
  deleted: "Attribut supprime",
  status_changed: "Statut mis a jour",
  target_changed: "Cible ajustee",
  target_description_changed: "Description mise a jour",
  comment_changed: "Commentaire mis a jour",
  name_changed: "Nom mis a jour",
  reordered: "Ordre ajuste",
  updated: "Attribut mis a jour",
};

const EMPTY_FORM = {
  name: "",
  target_value: "",
  target_description: "",
  current_status: "NC",
  current_comment: "",
  historyTimestamp: "",
};

const formatTimestamp = (value) => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const getMonthMeta = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-11
  const monthLabel = new Intl.DateTimeFormat("fr-FR", {
    month: "short",
    year: "numeric",
  }).format(d);

  // Key format: YYYY-MM (e.g. 2023-10) to sort easily
  const key = `${year}-${String(month + 1).padStart(2, "0")}`;

  return {
    key,
    label: monthLabel,
    year,
    month,
  };
};

const describeEntryTooltip = (entry) => {
  if (!entry) return "";
  const base = EVENT_LABELS[entry.event_type] || "Mise a jour";
  const payload = entry.payload || {};
  let detail = "";
  if (payload.comment) {
    detail = payload.comment;
  } else if (payload.new !== undefined) {
    detail = `${payload.old ?? "?"} -> ${payload.new ?? "?"}`;
  } else if (payload.target_value) {
    detail = `Cible: ${payload.target_value}`;
  } else if (payload.target_description) {
    detail = payload.target_description;
  }
  const author = entry.changed_by || entry.changed_by_email;
  const lines = [base, detail, author ? `par ${author}` : null].filter(Boolean);
  return lines.join("\n");
};

const describeTargetTooltip = (target) => {
  if (!target) return "";
  const lines = [
    `Objectif: ${target.target_status}`,
    target.iteration_name ? `Iteration: ${target.iteration_name}` : null,
    target.note || null,
  ].filter(Boolean);
  return lines.join("\n");
};

const StatusPicker = ({
  value,
  onChange,
  className = "",
  variant = "default",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selected = STATUS_DETAILS[value] || STATUS_DETAILS.NC;
  const isCompact = variant === "compact";

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const buttonClasses = isCompact
    ? "mt-1 flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500"
    : "mt-1 flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500";

  const badgeClasses = isCompact
    ? "rounded-full px-2 py-0.5 text-[11px] font-semibold"
    : "rounded-full px-3 py-1 text-xs font-semibold";

  const descriptionClasses = isCompact ? "text-[11px]" : "text-xs";

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`${buttonClasses} ${open ? "border-blue-500 ring-2 ring-blue-200" : ""}`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center justify-center ${badgeClasses} ${selected.badge}`}
          >
            {selected.label}
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-900">
              {selected.label}
            </p>
            <p className={`${descriptionClasses} text-gray-500`}>
              {selected.description}
            </p>
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-20 mt-2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
          {STATUS_ORDER.map((statusKey) => {
            const details = STATUS_DETAILS[statusKey];
            return (
              <button
                key={statusKey}
                type="button"
                onClick={() => {
                  onChange(statusKey);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none"
              >
                <div>
                  <p className="font-semibold text-gray-900">{details.label}</p>
                  <p className="text-xs text-gray-500">{details.description}</p>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${details.badge}`}
                >
                  {details.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const toDateTimeLocalValue = (input) => {
  if (!input) return "";
  const date =
    input instanceof Date ? new Date(input.getTime()) : new Date(input);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() - offset);
  return date.toISOString().slice(0, 16);
};

const AttributesPanel = ({
  attributes = [],
  history = [],
  roadmapTargets = [],
  error,
  isLoading,
  historyLoading,
  onAddAttribute,
  onUpdateAttribute,
  onUpdateStatus,
  onDeleteAttribute,
  onUpdateHistoryEntry,
  onDeleteHistoryEntry,
}) => {
  const [formValues, setFormValues] = useState(() => ({ ...EMPTY_FORM }));
  const [editingId, setEditingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [statusEditor, setStatusEditor] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState(null);

  // History editing state
  const [historyEditingId, setHistoryEditingId] = useState(null);
  const [historyEditValues, setHistoryEditValues] = useState({
    comment: "",
    changed_at: "",
  });
  const [historySaving, setHistorySaving] = useState(false);

  // Timeline editing state
  const [timelineEditingEntry, setTimelineEditingEntry] = useState(null); // { entry, attributeId, position: { top, left } }
  const [timelineEditValues, setTimelineEditValues] = useState({
    comment: "",
    changed_at: "",
  });

  const handleTimelineEntryClick = (e, entry, attributeId) => {
    if (!entry) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setTimelineEditingEntry({
      entry,
      attributeId,
      position: {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      },
    });
    setTimelineEditValues({
      comment: entry.payload?.comment || "",
      changed_at: entry.changed_at ? new Date(entry.changed_at).toISOString().slice(0, 16) : "",
    });
  };

  const handleTimelineEditClose = () => {
    setTimelineEditingEntry(null);
    setTimelineEditValues({ comment: "", changed_at: "" });
  };

  const handleTimelineSave = async () => {
    if (!timelineEditingEntry) return;
    setHistorySaving(true);
    try {
      await onUpdateHistoryEntry(
        timelineEditingEntry.attributeId,
        timelineEditingEntry.entry.id,
        {
          comment: timelineEditValues.comment,
          changed_at: timelineEditValues.changed_at || undefined,
        }
      );
      handleTimelineEditClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setHistorySaving(false);
    }
  };

  const handleTimelineDelete = async () => {
    if (!timelineEditingEntry) return;
    if (!window.confirm("Supprimer cette entrée d'historique ?")) return;
    setHistorySaving(true);
    try {
      await onDeleteHistoryEntry(
        timelineEditingEntry.attributeId,
        timelineEditingEntry.entry.id
      );
      handleTimelineEditClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setHistorySaving(false);
    }
  };

  const attributesPanelRef = useRef(null);

  const timelineData = useMemo(() => {
    const monthsMap = new Map();
    const ensureColumn = (meta) => {
      if (!monthsMap.has(meta.key)) {
        monthsMap.set(meta.key, {
          key: meta.key,
          label: meta.label,
          year: meta.year,
          month: meta.month,
          entries: [],
          targets: [],
        });
      }
      return monthsMap.get(meta.key);
    };

    // 1. Filter history to keep only entries for currently existing attributes
    const activeAttributeIds = new Set(attributes.map((a) => a.id));
    const filteredHistory = history.filter((entry) =>
      activeAttributeIds.has(entry.attribute_id)
    );

    filteredHistory.forEach((entry) => {
      if (!entry?.changed_at) return;
      const changedDate = new Date(entry.changed_at);
      if (Number.isNaN(changedDate.getTime())) return;
      const meta = getMonthMeta(changedDate);
      ensureColumn(meta).entries.push(entry);
    });

    (roadmapTargets || []).forEach((target) => {
      if (!target?.iteration_end_date) return;
      if (!activeAttributeIds.has(target.attribute_id)) return;

      const endDate = new Date(target.iteration_end_date);
      if (Number.isNaN(endDate.getTime())) return;
      const meta = getMonthMeta(endDate);
      ensureColumn(meta).targets.push(target);
    });

    const columns = [...monthsMap.values()].sort((a, b) =>
      a.key.localeCompare(b.key)
    );

    // Show last 12 months max
    const limitedColumns = columns.length > 12 ? columns.slice(-12) : columns;

    const preparedColumns = limitedColumns.map((column) => {
      const byAttribute = new Map();
      column.entries.forEach((entry) => {
        if (!entry.attribute_id) return;
        if (!byAttribute.has(entry.attribute_id))
          byAttribute.set(entry.attribute_id, []);
        byAttribute.get(entry.attribute_id).push(entry);
      });

      byAttribute.forEach((list, attrId) => {
        list.sort(
          (a, b) =>
            new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime(),
        );
        byAttribute.set(attrId, list);
      });

      const targetsByAttribute = new Map();
      column.targets.forEach((target) => {
        if (!target.attribute_id) return;
        targetsByAttribute.set(target.attribute_id, target);
      });

      return { ...column, byAttribute, targetsByAttribute };
    });

    return {
      columns: preparedColumns,
    };
  }, [history, roadmapTargets, attributes]);

  const resetForm = () => {
    setFormValues({ ...EMPTY_FORM });
    setEditingId(null);
  };

  const handleToggleForm = () => {
    if (isFormOpen) {
      resetForm();
    }
    setIsFormOpen((prev) => !prev);
    setLocalError(null);
  };

  const handleEditAttribute = (attribute) => {
    setFormValues({
      name: attribute.name || "",
      target_value: attribute.target_value || "",
      target_description: attribute.target_description || "",
      current_status: attribute.current_status || "NC",
      current_comment: attribute.current_comment || "",
      historyTimestamp: "",
    });
    setEditingId(attribute.id);
    setIsFormOpen(true);
    setLocalError(null);
  };

  const buildPayload = () => {
    const trimmedName = formValues.name.trim();
    if (!trimmedName) {
      setLocalError("Le nom de l'attribut est requis.");
      return null;
    }
    let historyISO = null;
    if (formValues.historyTimestamp) {
      const ts = new Date(formValues.historyTimestamp);
      if (Number.isNaN(ts.getTime())) {
        setLocalError("Horodatage invalide.");
        return null;
      }
      historyISO = ts.toISOString();
    }
    return {
      name: trimmedName,
      target_value: formValues.target_value?.trim() || null,
      target_description: formValues.target_description?.trim() || null,
      current_status: formValues.current_status,
      current_comment: formValues.current_comment?.trim() || null,
      ...(historyISO && { history_timestamp: historyISO }),
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      if (editingId) {
        await onUpdateAttribute?.(editingId, payload);
      } else {
        await onAddAttribute?.(payload);
      }
      resetForm();
      setIsFormOpen(false);
      setLocalError(null);
    } catch (err) {
      setLocalError(err?.message || "Impossible d'enregistrer l'attribut.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (attribute) => {
    if (!onDeleteAttribute) return;
    const confirmed = window.confirm(
      `Supprimer l'attribut "${attribute.name}" ?`,
    );
    if (!confirmed) return;
    setDeletingId(attribute.id);
    try {
      await onDeleteAttribute(attribute.id);
      setLocalError(null);
      if (editingId === attribute.id) {
        resetForm();
        setIsFormOpen(false);
      }
      if (statusEditor?.attributeId === attribute.id) {
        closeStatusEditor();
      }
    } catch (err) {
      setLocalError(err?.message || "Impossible de supprimer l'attribut.");
    } finally {
      setDeletingId(null);
    }
  };

  const closeStatusEditor = useCallback(() => {
    setStatusEditor(null);
    setStatusError(null);
    setStatusSaving(false);
  }, []);

  const openStatusEditor = (attribute, presetStatus) => {
    setStatusEditor({
      attributeId: attribute.id,
      status: presetStatus || attribute.current_status || "NC",
      comment: "",
      changedAt: toDateTimeLocalValue(new Date()),
    });
    setStatusError(null);
  };

  useEffect(() => {
    if (!statusEditor) return;
    const handleOutsideClick = (event) => {
      if (
        attributesPanelRef.current &&
        !attributesPanelRef.current.contains(event.target)
      ) {
        closeStatusEditor();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [statusEditor, closeStatusEditor]);

  useEffect(() => {
    if (!statusEditor) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeStatusEditor();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [statusEditor, closeStatusEditor]);

  const handleStatusSubmit = async () => {
    if (!statusEditor) return;
    const comment = statusEditor.comment.trim();
    if (!comment) {
      setStatusError("Un commentaire est requis.");
      return;
    }
    if (statusEditor.changedAt) {
      const dt = new Date(statusEditor.changedAt);
      if (Number.isNaN(dt.getTime())) {
        setStatusError("Horodatage invalide.");
        return;
      }
    }
    setStatusSaving(true);
    try {
      await onUpdateStatus?.(
        statusEditor.attributeId,
        statusEditor.status,
        comment,
        statusEditor.changedAt || null,
      );
      closeStatusEditor();
    } catch (err) {
      setStatusError(err?.message || "Impossible de mettre a jour le statut.");
    } finally {
      setStatusSaving(false);
    }
  };

  const handleHistoryEdit = (entry) => {
    setHistoryEditingId(entry.id);
    setHistoryEditValues({
      comment: entry.payload?.comment || "",
      changed_at: toDateTimeLocalValue(new Date(entry.changed_at)),
    });
  };

  const handleHistoryCancel = () => {
    setHistoryEditingId(null);
    setHistoryEditValues({ comment: "", changed_at: "" });
  };

  const handleHistorySave = async (attributeId, entryId) => {
    if (!onUpdateHistoryEntry) return;
    setHistorySaving(true);
    try {
      const payload = {
        comment: historyEditValues.comment,
        changed_at: historyEditValues.changed_at ? new Date(historyEditValues.changed_at).toISOString() : null,
      };
      await onUpdateHistoryEntry(attributeId, entryId, payload);
      setHistoryEditingId(null);
    } catch (err) {
      setLocalError(err?.message || "Erreur lors de la mise à jour de l'historique");
    } finally {
      setHistorySaving(false);
    }
  };

  const handleHistoryDeleteList = async (attributeId, entryId) => {
    if (!onDeleteHistoryEntry) return;
    if (!window.confirm("Supprimer cette entrée d'historique ?")) return;
    try {
      await onDeleteHistoryEntry(attributeId, entryId);
    } catch (err) {
      setLocalError(err?.message || "Erreur lors de la suppression de l'historique");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <section
        ref={attributesPanelRef}
        className="flex w-full flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all duration-300"
      >
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Tableau des attributs
            </h3>
            <p className="text-sm text-gray-500">
              Definissez vos cibles et suivez l'etat de conformite du projet.
            </p>
          </div>
          <button
            onClick={handleToggleForm}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-500"
          >
            <Plus className="h-4 w-4" />
            {isFormOpen ? "Fermer" : "Nouvel attribut"}
          </button>
        </div>

        {(error || localError) && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {localError || error}
          </div>
        )}

        {/* MATRIX LAYOUT */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                {/* Sticky Columns Header */}
                <th className="sticky left-0 z-20 w-[200px] bg-gray-50 px-3 py-2 text-left shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Attribut
                </th>
                <th className="sticky left-[200px] z-20 w-[120px] bg-gray-50 px-3 py-2 text-left shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Cible
                </th>
                <th className="sticky left-[320px] z-20 w-[100px] bg-gray-50 px-3 py-2 text-left shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Statut
                </th>

                {/* Timeline Columns Header */}
                {timelineData.columns.map((column) => (
                  <th
                    key={column.key}
                    className="min-w-[80px] border-l border-gray-100 px-2 py-2 text-center"
                  >
                    <div className="flex flex-col items-center">
                      <span>{column.label}</span>
                    </div>
                  </th>
                ))}

                {/* Sticky Right Delete Column Header */}
                <th className="sticky right-0 z-20 bg-gray-50 w-[50px] shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {attributes.map((attribute) => {
                const activeStatus = attribute.current_status || STATUS_ORDER[0];
                const statusDetails = STATUS_DETAILS[activeStatus] || STATUS_DETAILS.NC;

                return (
                  <React.Fragment key={attribute.id}>
                    <tr className="group hover:bg-gray-50/50 transition-colors">
                      {/* Sticky Columns Body */}
                      <td
                        className="sticky left-0 z-10 cursor-pointer bg-white px-3 py-2 font-medium text-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] transition-colors hover:text-blue-600"
                        onClick={() => handleEditAttribute(attribute)}
                      >
                        <div className="truncate max-w-[180px]" title={attribute.name}>
                          {attribute.name}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          Maj: {formatTimestamp(attribute.updated_at)}
                        </div>
                      </td>
                      <td
                        className="sticky left-[200px] z-10 cursor-pointer bg-white px-3 py-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] transition-colors hover:text-blue-600"
                        onClick={() => handleEditAttribute(attribute)}
                      >
                        <div className="text-sm text-gray-700 truncate max-w-[100px]" title={attribute.target_value}>
                          {attribute.target_value || "-"}
                        </div>
                      </td>
                      <td className="sticky left-[320px] z-10 bg-white px-3 py-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <button
                          type="button"
                          onClick={() => openStatusEditor(attribute)}
                          className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition hover:opacity-80 ${statusDetails.badge}`}
                        >
                          {statusDetails.label}
                        </button>
                      </td>

                      {/* Timeline Columns Body */}
                      {timelineData.columns.map((column) => {
                        const entries = column.byAttribute.get(attribute.id) || [];
                        const latestEntry = entries[0];
                        const target = column.targetsByAttribute?.get(attribute.id);

                        let cellContent = null;

                        if (latestEntry) {
                          const statusKey = latestEntry.status_after || attribute.current_status || "NC";
                          const entryDetails = STATUS_DETAILS[statusKey] || STATUS_DETAILS.NC;
                          const tooltipText = describeEntryTooltip(latestEntry);

                          cellContent = (
                            <div
                              className={`mx-auto flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-[10px] font-bold shadow-sm transition hover:scale-110 ${entryDetails.badge}`}
                              title={tooltipText}
                              onClick={(e) => handleTimelineEntryClick(e, latestEntry, attribute.id)}
                            >
                              {entryDetails.label}
                            </div>
                          );
                        } else if (target) {
                          const targetStatusKey = target.target_status || "NC";
                          const targetDetails = STATUS_DETAILS[targetStatusKey] || STATUS_DETAILS.NC;
                          const tooltipText = describeTargetTooltip(target);

                          cellContent = (
                            <div
                              className={`mx-auto flex h-7 w-7 cursor-help items-center justify-center rounded-full text-[10px] font-bold opacity-80 transition hover:scale-110 ${targetDetails.roadmapBadge}`}
                              title={tooltipText}
                            >
                              {targetDetails.label}
                            </div>
                          );
                        }

                        return (
                          <td key={column.key} className="border-l border-gray-100 px-2 py-2 text-center">
                            {cellContent}
                          </td>
                        );
                      })}

                      {/* Sticky Right Delete Button (Visible on Hover) */}
                      <td className="sticky right-0 z-10 bg-white/0 px-2 py-2 text-center shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <button
                          onClick={() => handleDelete(attribute)}
                          className="invisible group-hover:visible rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-all"
                          title="Supprimer l'attribut"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>

                    {/* Status Editor Row (Inline) */}
                    {statusEditor?.attributeId === attribute.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={4 + timelineData.columns.length} className="sticky left-0 z-30 px-3 py-3">
                          <div className="mx-auto max-w-3xl space-y-3 rounded-xl border border-dashed border-gray-300 bg-white/90 p-3 shadow-lg">
                            <div className="grid gap-3 md:grid-cols-3">
                              <div>
                                <label className="text-sm font-medium text-gray-700">
                                  Nouveau statut
                                </label>
                                <StatusPicker
                                  variant="compact"
                                  value={statusEditor.status}
                                  onChange={(nextStatus) =>
                                    setStatusEditor((prev) => ({
                                      ...prev,
                                      status: nextStatus,
                                    }))
                                  }
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700">
                                  Horodatage
                                </label>
                                <input
                                  type="datetime-local"
                                  value={statusEditor.changedAt || ""}
                                  onChange={(e) =>
                                    setStatusEditor((prev) => ({
                                      ...prev,
                                      changedAt: e.target.value,
                                    }))
                                  }
                                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">
                                Commentaire
                              </label>
                              <textarea
                                value={statusEditor.comment}
                                onChange={(e) =>
                                  setStatusEditor((prev) => ({
                                    ...prev,
                                    comment: e.target.value,
                                  }))
                                }
                                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Expliquez rapidement le contexte du changement."
                                rows={2}
                              />
                            </div>
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={closeStatusEditor}
                                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-white"
                              >
                                Annuler
                              </button>
                              <button
                                type="button"
                                onClick={handleStatusSubmit}
                                disabled={statusSaving}
                                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                Valider
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Attribute Edit Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                <h4 className="text-xl font-semibold text-gray-900">
                  {editingId ? "Modifier l'attribut" : "Nouvel attribut"}
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsFormOpen(false);
                  }}
                  className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    value={formValues.name}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Temperature survie max"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Cible
                  </label>
                  <input
                    type="text"
                    value={formValues.target_value}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        target_value: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 250°C"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">
                    Description cible
                  </label>
                  <textarea
                    value={formValues.target_description}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        target_description: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Details techniques..."
                    rows={3}
                  />
                </div>
                {!editingId && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Statut initial
                      </label>
                      <StatusPicker
                        value={formValues.current_status}
                        onChange={(val) =>
                          setFormValues((prev) => ({
                            ...prev,
                            current_status: val,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Commentaire initial
                      </label>
                      <textarea
                        value={formValues.current_comment}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            current_comment: e.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* History Editing Section (Only when editing an existing attribute) */}
              {editingId && (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <h5 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <History className="h-4 w-4 text-gray-500" />
                    Historique des modifications
                  </h5>
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                    {history
                      .filter((h) => h.attribute_id === editingId)
                      .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))
                      .map((entry) => (
                        <div
                          key={entry.id}
                          className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm"
                        >
                          {historyEditingId === entry.id ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div>
                                  <label className="text-xs font-medium text-gray-500">
                                    Date
                                  </label>
                                  <input
                                    type="datetime-local"
                                    value={historyEditValues.changed_at}
                                    onChange={(e) =>
                                      setHistoryEditValues((prev) => ({
                                        ...prev,
                                        changed_at: e.target.value,
                                      }))
                                    }
                                    className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-xs"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-gray-500">
                                    Commentaire
                                  </label>
                                  <textarea
                                    value={historyEditValues.comment}
                                    onChange={(e) =>
                                      setHistoryEditValues((prev) => ({
                                        ...prev,
                                        comment: e.target.value,
                                      }))
                                    }
                                    className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-xs"
                                    rows={2}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={handleHistoryCancel}
                                  className="rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
                                  type="button"
                                >
                                  Annuler
                                </button>
                                <button
                                  onClick={() => handleHistorySave(editingId, entry.id)}
                                  disabled={historySaving}
                                  className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                                  type="button"
                                >
                                  Enregistrer
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    {EVENT_LABELS[entry.event_type] || "Modification"}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatTimestamp(entry.changed_at)}
                                  </span>
                                </div>
                                <p className="mt-1 text-gray-600">
                                  {entry.payload?.comment ||
                                    (entry.payload?.new
                                      ? `${entry.payload.old || "?"} → ${entry.payload.new}`
                                      : "Aucun détail")}
                                </p>
                              </div>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleHistoryEdit(entry)}
                                  className="rounded p-1 text-gray-400 hover:bg-white hover:text-blue-600 hover:shadow-sm"
                                  title="Modifier"
                                  type="button"
                                >
                                  <Edit3 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handleHistoryDeleteList(editingId, entry.id)}
                                  className="rounded p-1 text-gray-400 hover:bg-white hover:text-red-600 hover:shadow-sm"
                                  title="Supprimer"
                                  type="button"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    {history.filter((h) => h.attribute_id === editingId).length ===
                      0 && (
                        <p className="text-center text-xs text-gray-500">
                          Aucun historique disponible.
                        </p>
                      )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsFormOpen(false);
                  }}
                  className="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Timeline Edit Popover */}
      {timelineEditingEntry && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={handleTimelineEditClose}
          />
          <div
            className="fixed z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
            style={{
              top: Math.min(timelineEditingEntry.position.top + 10, window.innerHeight - 300),
              left: Math.min(timelineEditingEntry.position.left, window.innerWidth - 300),
            }}
          >
            <h5 className="mb-3 text-sm font-semibold text-gray-900">
              Modifier l'historique
            </h5>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700">Date</label>
                <input
                  type="datetime-local"
                  value={timelineEditValues.changed_at}
                  onChange={(e) =>
                    setTimelineEditValues((prev) => ({
                      ...prev,
                      changed_at: e.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Commentaire</label>
                <textarea
                  value={timelineEditValues.comment}
                  onChange={(e) =>
                    setTimelineEditValues((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  rows={3}
                />
              </div>
              <div className="flex justify-between pt-2">
                <button
                  onClick={handleTimelineDelete}
                  className="rounded p-1.5 text-red-600 hover:bg-red-50"
                  title="Supprimer"
                  disabled={historySaving}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleTimelineEditClose}
                    className="rounded px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    disabled={historySaving}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleTimelineSave}
                    className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                    disabled={historySaving}
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AttributesPanel;
